'use client'

import { memo, type ReactNode } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion'
import { cn } from '@/lib/utils'

const STARRY_STARS = [
  { cx: 120, cy: 80, r: 2.2, delay: 0 },
  { cx: 280, cy: 48, r: 1.6, delay: 0.4 },
  { cx: 420, cy: 96, r: 2.8, delay: 0.8 },
  { cx: 880, cy: 64, r: 2, delay: 1.2 },
  { cx: 1020, cy: 112, r: 1.4, delay: 0.6 },
  { cx: 760, cy: 36, r: 1.8, delay: 1.6 },
  { cx: 540, cy: 72, r: 1.2, delay: 2 },
  { cx: 960, cy: 140, r: 1.6, delay: 0.2 },
] as const

/** Van Gogh swirl sky: moon glow + cobalt strokes */
export const NinxlStarrySwirls = memo(function NinxlStarrySwirls({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 1200 600" className={cn('ninxl-starry-swirls h-full w-full', className)} aria-hidden preserveAspectRatio="xMidYMin slice">
      <g opacity="0.45">
        <path d="M80 420 Q280 180 520 320 T960 240" className="ninxl-starry-swirl" stroke="var(--ninxl-ultramarine)" strokeWidth="2.5" strokeOpacity="0.35" />
        <path d="M200 500 Q420 260 640 380 T1080 300" className="ninxl-starry-swirl" stroke="var(--ninxl-starry-cyan)" strokeWidth="2" strokeOpacity="0.3" style={{ animationDelay: '2s' }} />
        <path d="M0 360 Q220 120 480 280 T920 180" className="ninxl-starry-swirl" stroke="var(--ninxl-ultramarine-light)" strokeWidth="1.8" strokeOpacity="0.28" style={{ animationDelay: '4s' }} />
        <path d="M140 280 Q360 80 600 200 T1000 120" className="ninxl-starry-swirl" stroke="var(--ninxl-ultramarine)" strokeWidth="2.2" strokeOpacity="0.32" style={{ animationDelay: '6s' }} />
      </g>
    </svg>
  )
})

/** Scattered star halos across the hero sky */
export const NinxlStarField = memo(function NinxlStarField({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 1200 400" className={cn('ninxl-starry-field h-full w-full', className)} aria-hidden preserveAspectRatio="xMidYMin slice">
      {STARRY_STARS.map((star) => (
        <g key={`${star.cx}-${star.cy}`}>
          <circle
            cx={star.cx}
            cy={star.cy}
            r={star.r * 3}
            fill="var(--ninxl-gilded-light)"
            fillOpacity="0.12"
            className="ninxl-starry-star"
            style={{ animationDelay: `${star.delay}s` }}
          />
          <circle
            cx={star.cx}
            cy={star.cy}
            r={star.r}
            fill="var(--ninxl-gilded)"
            fillOpacity="0.75"
            className="ninxl-starry-star"
            style={{ animationDelay: `${star.delay}s` }}
          />
        </g>
      ))}
    </svg>
  )
})

/** Bright apex glow: divine light beam from the top edge */
export const NinxlTopFlash = memo(function NinxlTopFlash({ className }: { className?: string }) {
  return <div className={cn('ninxl-hero-top-flash pointer-events-none absolute inset-x-0 top-0', className)} aria-hidden />
})

/** Moonlit rays: star gold center + cobalt depth */
export const NinxlDivineRays = memo(function NinxlDivineRays({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 1200 600"
      className={cn('ninxl-divine-rays h-full w-full', className)}
      aria-hidden
      preserveAspectRatio="xMidYMin slice"
    >
      <defs>
        <linearGradient id="ninxl-ray-warm" x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="var(--ninxl-gilded-light)" stopOpacity="0.72" />
          <stop offset="35%" stopColor="var(--ninxl-gilded)" stopOpacity="0.28" />
          <stop offset="55%" stopColor="var(--ninxl-gilded)" stopOpacity="0.14" />
          <stop offset="100%" stopColor="var(--ninxl-brand-teal)" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="ninxl-ray-cool" x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="var(--ninxl-ultramarine-light)" stopOpacity="0.38" />
          <stop offset="100%" stopColor="var(--ninxl-ultramarine)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g className="ninxl-divine-rays-gold" opacity="0.68">
        <path d="M600 0 L520 420 L600 380 Z" fill="url(#ninxl-ray-warm)" />
        <path d="M600 0 L680 420 L600 380 Z" fill="url(#ninxl-ray-warm)" />
        <path d="M600 0 L440 480 L560 400 Z" fill="url(#ninxl-ray-warm)" opacity="0.7" />
        <path d="M600 0 L760 480 L640 400 Z" fill="url(#ninxl-ray-warm)" opacity="0.7" />
        <path d="M600 0 L320 520 L500 420 Z" fill="url(#ninxl-ray-warm)" opacity="0.45" />
        <path d="M600 0 L880 520 L700 420 Z" fill="url(#ninxl-ray-warm)" opacity="0.45" />
      </g>
      <g className="ninxl-divine-rays-teal" opacity="0.35">
        <path d="M600 0 L560 360 L600 340 Z" fill="url(#ninxl-ray-cool)" />
        <path d="M600 0 L640 360 L600 340 Z" fill="url(#ninxl-ray-cool)" />
      </g>
      <g className="ninxl-divine-rays-fine" opacity="0.25" stroke="var(--ninxl-ultramarine-light)" strokeWidth="0.6">
        <line x1="600" y1="0" x2="480" y2="500" />
        <line x1="600" y1="0" x2="720" y2="500" />
        <line x1="600" y1="0" x2="360" y2="540" />
        <line x1="600" y1="0" x2="840" y2="540" />
      </g>
      <circle cx="600" cy="0" r="48" fill="var(--ninxl-gilded-light)" fillOpacity="0.5" />
      <circle cx="600" cy="52" r="22" fill="var(--ninxl-gilded-light)" fillOpacity="0.65" className="ninxl-fresco-glow" />
      <circle cx="600" cy="52" r="36" fill="var(--ninxl-gilded)" fillOpacity="0.28" className="ninxl-fresco-glow" style={{ animationDelay: '0.6s' }} />
      <circle cx="600" cy="52" r="56" fill="var(--ninxl-ultramarine-light)" fillOpacity="0.12" />
      <circle cx="600" cy="52" r="76" fill="var(--ninxl-ultramarine)" fillOpacity="0.06" />
    </svg>
  )
})

/** Sistine-style cloud scroll band */
export const NinxlCloudScrollBand = memo(function NinxlCloudScrollBand({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 1200 80" className={cn('ninxl-cloud-scroll h-full w-full', className)} aria-hidden preserveAspectRatio="none">
      <defs>
        <linearGradient id="ninxl-cloud-grad" x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor="var(--ninxl-fresco-ivory)" stopOpacity="0" />
          <stop offset="15%" stopColor="var(--ninxl-fresco-plaster)" stopOpacity="0.5" />
          <stop offset="50%" stopColor="var(--ninxl-fresco-mist)" stopOpacity="0.65" />
          <stop offset="85%" stopColor="var(--ninxl-fresco-plaster)" stopOpacity="0.5" />
          <stop offset="100%" stopColor="var(--ninxl-fresco-ivory)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M0 48 C120 20, 240 68, 360 40 S600 24, 720 44 S960 64, 1080 36 S1200 52, 1200 48 L1200 80 L0 80 Z"
        fill="url(#ninxl-cloud-grad)"
      />
      <path
        d="M0 52 Q200 28 400 50 T800 46 T1200 54"
        fill="none"
        stroke="var(--ninxl-ultramarine)"
        strokeWidth="1"
        strokeOpacity="0.28"
      />
      {[120, 300, 480, 660, 840, 1020].map((x, i) => (
        <ellipse key={x} cx={x} cy={40 + (i % 2) * 6} rx={40 + (i % 3) * 8} ry={12 + (i % 2) * 4} fill="var(--ninxl-fresco-mist)" fillOpacity={0.32 + (i % 2) * 0.08} />
      ))}
    </svg>
  )
})

/** Laurel wreath crest: richer section ornament */
export const NinxlLaurelCrest = memo(function NinxlLaurelCrest({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 240 36" className={cn('ninxl-laurel-crest mx-auto h-9 w-56', className)} aria-hidden>
      <path d="M4 18 H88" stroke="var(--ninxl-gilded)" strokeWidth="1.2" strokeOpacity="0.5" />
      <path d="M152 18 H236" stroke="var(--ninxl-gilded)" strokeWidth="1.2" strokeOpacity="0.5" />
      <path d="M88 18 Q100 8 112 14 Q118 20 120 18 Q122 16 128 14 Q140 8 152 18" fill="none" stroke="var(--ninxl-gilded)" strokeWidth="1.4" strokeOpacity="0.65" />
      <ellipse cx="120" cy="18" rx="10" ry="8" fill="var(--ninxl-gilded-light)" fillOpacity="0.35" stroke="var(--ninxl-gilded)" strokeWidth="1" strokeOpacity="0.5" />
      <path d="M96 16 Q88 10 82 16 Q88 20 96 16" fill="var(--ninxl-ultramarine)" fillOpacity="0.2" />
      <path d="M144 16 Q152 10 158 16 Q152 20 144 16" fill="var(--ninxl-ultramarine)" fillOpacity="0.2" />
      <path d="M104 12 Q108 8 112 12" fill="none" stroke="var(--ninxl-ultramarine-light)" strokeWidth="0.8" strokeOpacity="0.5" />
      <path d="M128 12 Q132 8 136 12" fill="none" stroke="var(--ninxl-ultramarine-light)" strokeWidth="0.8" strokeOpacity="0.5" />
      <circle cx="120" cy="18" r="3" fill="var(--ninxl-gilded)" fillOpacity="0.55" className="ninxl-fresco-glow" />
    </svg>
  )
})

/** Cartouche ornament for section headers */
export const NinxlGildedOrnament = memo(function NinxlGildedOrnament({ className }: { className?: string }) {
  return <NinxlLaurelCrest className={cn('h-7 w-52', className)} />
})

/** Classical arch framing the hero mock: baroque proscenium */
export const NinxlFrescoArch = memo(function NinxlFrescoArch({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 800 120"
      className={cn('ninxl-fresco-arch pointer-events-none absolute -top-8 left-1/2 z-10 h-16 w-[108%] -translate-x-1/2', className)}
      aria-hidden
      preserveAspectRatio="none"
    >
      <path
        d="M40 110 Q400 -20 760 110"
        fill="none"
        stroke="var(--ninxl-ultramarine)"
        strokeWidth="2"
        strokeOpacity="0.22"
      />
      <path
        d="M56 108 Q400 4 744 108"
        fill="none"
        stroke="var(--ninxl-gilded)"
        strokeWidth="1.2"
        strokeOpacity="0.35"
      />
      <path d="M32 110 H768" stroke="var(--ninxl-gilded-light)" strokeWidth="1" strokeOpacity="0.25" />
      <circle cx="400" cy="28" r="6" fill="var(--ninxl-gilded-light)" fillOpacity="0.4" />
    </svg>
  )
})

/** Marble vein wash behind hero mock */
export const NinxlMarbleVeil = memo(function NinxlMarbleVeil({ className }: { className?: string }) {
  return (
    <div className={cn('ninxl-marble-veil pointer-events-none absolute -inset-6 -z-20 rounded-[2.75rem]', className)} aria-hidden>
      <svg viewBox="0 0 400 300" className="h-full w-full opacity-50" preserveAspectRatio="none">
        <defs>
          <linearGradient id="ninxl-marble-base" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--ninxl-fresco-ivory)" stopOpacity="0.8" />
            <stop offset="50%" stopColor="var(--ninxl-fresco-plaster)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="var(--ninxl-fresco-mist)" stopOpacity="0.7" />
          </linearGradient>
        </defs>
        <rect width="400" height="300" fill="url(#ninxl-marble-base)" rx="32" />
        <path d="M0 80 Q120 40 200 90 T400 70" fill="none" stroke="var(--ninxl-ultramarine-light)" strokeWidth="1.5" strokeOpacity="0.12" />
        <path d="M0 160 Q160 120 280 170 T400 150" fill="none" stroke="var(--ninxl-gilded-light)" strokeWidth="1" strokeOpacity="0.1" />
        <path d="M0 220 Q100 200 220 240 T400 210" fill="none" stroke="var(--ninxl-ultramarine)" strokeWidth="0.8" strokeOpacity="0.08" />
      </svg>
    </div>
  )
})

/** Double-line fresco frame for featured blocks */
export const NinxlFrescoFrame = memo(function NinxlFrescoFrame({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('ninxl-fresco-frame relative', className)}>
      <span className="ninxl-fresco-frame-corner ninxl-fresco-frame-corner-tl" aria-hidden />
      <span className="ninxl-fresco-frame-corner ninxl-fresco-frame-corner-tr" aria-hidden />
      <span className="ninxl-fresco-frame-corner ninxl-fresco-frame-corner-bl" aria-hidden />
      <span className="ninxl-fresco-frame-corner ninxl-fresco-frame-corner-br" aria-hidden />
      {children}
    </div>
  )
})

/** Classical pilaster accents for major sections */
export const NinxlClassicalPilasters = memo(function NinxlClassicalPilasters({ className }: { className?: string }) {
  return (
    <div className={cn('ninxl-classical-pilasters pointer-events-none absolute inset-y-8 inset-x-0 hidden xl:block', className)} aria-hidden>
      <svg viewBox="0 0 48 320" className="absolute left-4 top-0 h-full w-12 opacity-30">
        <rect x="16" y="0" width="16" height="320" fill="var(--ninxl-fresco-plaster)" fillOpacity="0.45" />
        <rect x="18" y="0" width="12" height="320" fill="none" stroke="var(--ninxl-ultramarine)" strokeWidth="1" strokeOpacity="0.35" />
        <path d="M12 0 H36 V16 H12 Z M12 304 H36 V320 H12 Z" fill="var(--ninxl-gilded-light)" fillOpacity="0.28" stroke="var(--ninxl-gilded)" strokeWidth="0.8" strokeOpacity="0.4" />
      </svg>
      <svg viewBox="0 0 48 320" className="absolute right-4 top-0 h-full w-12 opacity-30 -scale-x-100">
        <rect x="16" y="0" width="16" height="320" fill="var(--ninxl-fresco-plaster)" fillOpacity="0.45" />
        <rect x="18" y="0" width="12" height="320" fill="none" stroke="var(--ninxl-ultramarine)" strokeWidth="1" strokeOpacity="0.35" />
        <path d="M12 0 H36 V16 H12 Z M12 304 H36 V320 H12 Z" fill="var(--ninxl-gilded-light)" fillOpacity="0.28" stroke="var(--ninxl-gilded)" strokeWidth="0.8" strokeOpacity="0.4" />
      </svg>
    </div>
  )
})

/** Page-wide fresco atmosphere: grain, chiaroscuro, sfumato */
export const NinxlRenaissanceAtmosphere = memo(function NinxlRenaissanceAtmosphere() {
  return (
    <div className="ninxl-renaissance-atmosphere pointer-events-none fixed inset-0 -z-[8]" aria-hidden>
      <div className="ninxl-chiaroscuro-vignette absolute inset-0" />
      <div className="ninxl-oil-glaze absolute inset-0" />
      <div className="ninxl-fresco-grain absolute inset-0" />
      <div className="ninxl-sfumato-wash absolute inset-0" />
      <div className="ninxl-sfumato-wash-alt absolute inset-0" />
      <div className="ninxl-starry-field absolute inset-x-0 top-0 h-[55vh] opacity-70">
        <NinxlStarField />
      </div>
    </div>
  )
})

/** Hero fresco stack: rays + clouds with scroll parallax */
export const NinxlHeroFrescoLayer = memo(function NinxlHeroFrescoLayer() {
  const shouldReduce = useReducedMotion()
  const { scrollYProgress } = useScroll()
  const rayY = useTransform(scrollYProgress, [0, 0.35], [0, -48])
  const cloudY = useTransform(scrollYProgress, [0, 0.35], [0, 24])

  return (
    <>
      <motion.div
        className="absolute inset-x-0 top-0 h-[min(72vh,640px)]"
        style={{ y: shouldReduce ? 0 : rayY }}
      >
        <NinxlStarrySwirls className="opacity-80" />
        <NinxlStarField className="opacity-85" />
        <NinxlDivineRays className="opacity-90" />
        <NinxlTopFlash className="h-[min(48vh,420px)]" />
      </motion.div>
      <motion.div
        className="absolute inset-x-0 top-[18%] h-20 opacity-80"
        style={{ y: shouldReduce ? 0 : cloudY }}
      >
        <NinxlCloudScrollBand />
      </motion.div>
    </>
  )
})